import type { AdminInquiryDetail } from '@models/BookingInquiry';
import type { CustomsInstruction } from '@models/next/CustomsInstruction';
import type {
  CustomsRequestForm,
  CustomsRequestSubmissionTarget,
} from '@models/next/CustomsRequest';
import type { PreAlertDetails } from '@models/next/PreAlert';
import { createStrings } from '@utils/local';
import type { Either } from 'fp-ts/lib/Either';

export const customsRequestStrings = createStrings({
  ja: {
    customsRequest: '通関依頼書',
    d2d_no: 'D2D番号',
    cargo_move_in_date: '貨物搬入日',
    free_time: 'Free Time',
    tentative_pickup_date: '集荷日（仮）',
    container_no: 'コンテナ番号',
    memo: '備考メモ',
    status: '通関状況',
    creator: '作成者',
    check_type: '検査',
    createdAt: '作成時間',
    submittedAt: '送信時間',
    changeLog: '変更履歴',
    submissionTarget: '送信先',
    errorPreAlertNoEta: 'この案件のETAが確認されていません。',
    errorPreAlertNoArrivalNotice: 'この案件のArrival Noticeが作成されていません。',
    errorPreAlertNoSeaWaybill: 'この案件のSea Waybillが作成されていません。',
    customsAgent: '通関業者',
    errorCannotSubmit: (lastStatus: string) =>
      `この案件は「${lastStatus}」のでもう一度提出することができません。`,

    importer_code: '輸出入者符号',
    real_time_account: 'リアルタイム口座番号',
    deferred_payment_account: '延納番号',
    special_note: '特記事項',
  },
});

export const resolveCustomsRequestInitialValues = (
  preAlert: PreAlertDetails,
  bookingInquiry: AdminInquiryDetail,
  customsInstruction: CustomsInstruction,
  initialDateValues: Pick<
    CustomsRequestForm,
    'cargo_move_in_date' | 'free_time' | 'tentative_pickup_date'
  >,
): Either<{ messages: string[] }, CustomsRequestForm> => {
  const messages = [];

  // Because PreAlert vessel information must be included
  if (!preAlert.eta) {
    messages.push(customsRequestStrings.errorPreAlertNoEta);
  }

  // Because Arrival Notice must be included in 通関資料, and there are data included in the
  // customs request PDF.
  if (!preAlert.arrival_notice_id) {
    messages.push(customsRequestStrings.errorPreAlertNoArrivalNotice);
  }

  if (messages.length > 0) {
    return {
      _tag: 'Left',
      left: { messages },
    };
  }

  return {
    _tag: 'Right',
    right: {
      cargo_move_in_date: initialDateValues.cargo_move_in_date,
      free_time: initialDateValues.free_time,
      tentative_pickup_date: initialDateValues.tentative_pickup_date,
      container_no: '',
      memo: '',
      owner_company: {
        importer_code: bookingInquiry.owner_company?.importer_code || '',
        real_time_account: bookingInquiry.owner_company?.real_time_account || '',
        deferred_payment_account: bookingInquiry.owner_company?.deferred_payment_account || '',
      },
      /**
       * @see: should match how we display in BookingInquiryDisplay
       */
      special_note: customsInstruction.special_note
        .filter(({ selected }) => selected)
        .flatMap(({ title, note }) => [title, note.trim().length > 0 ? note.trim() : '-'])
        .join('\n'),
      submission_target_company: '',
      action: 'save',
    },
  };
};

// Don't send actual email in non-production env;
const makeEmailTarget = (email: string) => {
  if (import.meta.env.VITE_APP_STAGE === 'production' || import.meta.env.VITE_APP_STAGE === 'e2e') {
    return email;
  } else {
    const beforeAt = email.split('@')[0];
    return `wangchenyu2017+${beforeAt}@gmail.com`;
  }
};

export const allCustomsRequestSubmissionInternalTargets: CustomsRequestSubmissionTarget[] = [
  {
    emails: [makeEmailTarget('osakatsukan@fcstandard.co.jp')],
    company: 'FC自社（大阪）',
    type: 'INTERNAL',
    telephone: '06-6266-8158',
  },
  {
    emails: [makeEmailTarget('tokyotsukan@fcstandard.co.jp')],
    company: 'FC自社（東京）',
    type: 'INTERNAL',
    telephone: '03-5565-8818',
  },
];
